import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../context/ThemeContext';
import toast from '../../utils/toast';

const BookingSuccessPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark } = useTheme();
  const { t } = useTranslation('booking');
  
  const [bookingData, setBookingData] = useState(null);

  useEffect(() => {
    // Get booking data from navigation state
    if (location.state?.booking) {
      setBookingData(location.state);
    } else {
      // If no state, redirect to bookings page
      navigate('/app/bookings');
    }
  }, [location.state, navigate]);

  const getSalonTimezone = () => bookingData?.timezone || bookingData?.booking?.timezone || 'UTC';

  const formatDateTime = (dateTimeStr) => {
    const timezone = getSalonTimezone();
    const date = new Date(dateTimeStr);
    const weekday = date.toLocaleDateString('en-US', { weekday: 'long', timeZone: timezone });
    const dateStr = date.toLocaleDateString('en-US', { 
      day: 'numeric', 
      month: 'short',
      timeZone: timezone
    });
    const timeStr = date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true,
      timeZone: timezone
    });
    
    return `${weekday}, ${dateStr} at ${timeStr}`;
  };

  const generateICS = () => {
    if (!bookingData) return;
    
    const { booking, barber, service, appointmentTime } = bookingData;
    const startDate = new Date(appointmentTime);
    const endDate = new Date(startDate.getTime() + (service.duration_min * 60000));
    
    const formatICSDate = (date) => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };
    
    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Hairchitech//Booking System//EN',
      'BEGIN:VEVENT',
      `UID:${booking.id}@hairchitech.com`,
      `DTSTART:${formatICSDate(startDate)}`,
      `DTEND:${formatICSDate(endDate)}`,
      `SUMMARY:Hairchitech Appointment - ${service.name}`,
      `DESCRIPTION:Appointment with ${barber.name}\\nService: ${service.name}\\nDuration: ${service.duration_min} minutes`,
      `LOCATION:Hairchitech Salon`,
      `STATUS:CONFIRMED`,
      `ORGANIZER:CN=Hairchitech:mailto:bookings@hairchitech.com`,
      'END:VEVENT',
      'END:VCALENDAR'
    ].join('\r\n');
    
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `hairchitech-appointment-${booking.id}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success('Calendar event downloaded');
    
    // Fire analytics event
    if (window.gtag) {
      window.gtag('event', 'calendar_downloaded', {
        booking_id: booking.id,
        barber_id: barber.id,
        service_id: service.id
      });
    }
  };

  const handleViewBookings = () => {
    navigate('/app/bookings');
    
    // Fire analytics event
    if (window.gtag) {
      window.gtag('event', 'view_bookings_clicked', {
        booking_id: bookingData?.booking?.id
      });
    }
  };

  const handleNewBooking = () => {
    navigate('/app/book');
    
    // Fire analytics event
    if (window.gtag) {
      window.gtag('event', 'new_booking_clicked', {
        previous_booking_id: bookingData?.booking?.id
      });
    }
  };

  if (!bookingData) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className={`text-center py-12 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          <div className="animate-pulse">
            <div className="h-16 w-16 bg-gray-300 rounded-full mx-auto mb-4"></div>
            <div className="h-8 w-64 bg-gray-300 rounded mx-auto mb-2"></div>
            <div className="h-4 w-48 bg-gray-300 rounded mx-auto"></div>
          </div>
        </div>
      </div>
    );
  }

  const { booking, barber, service, appointmentTime } = bookingData;

  return (
    <div className="max-w-4xl mx-auto">
      {/* Success Header */}
      <div className="text-center mb-8">
        <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
          isDark ? 'bg-green-900/20' : 'bg-green-100'
        }`}>
          <svg className={`h-12 w-12 ${isDark ? 'text-green-400' : 'text-green-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        
        <h1 className={`text-3xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          {t('booking.success_title')}
        </h1>
        <p className={`text-lg ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
          {t('booking.success_subtitle')}
        </p>
      </div>

      {/* Booking Summary */}
      <div className={`p-6 rounded-lg border mb-8 ${
        isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      }`}>
        <h2 className={`text-xl font-semibold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          {t('booking.appointment_details')}
        </h2>
        
        <div className="space-y-6">
          {/* Barber */}
          <div className="flex items-center space-x-4">
            <div 
              className="w-16 h-16 rounded-full flex items-center justify-center text-white font-semibold text-xl"
              style={{ backgroundColor: barber.color_hex || '#3B82F6' }}
            >
              {barber.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <h3 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {barber.name}
              </h3>
              <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                {t('booking.your_barber')}
              </p>
            </div>
          </div>

          {/* Service */}
          <div className="flex items-start space-x-4">
            <div className={`w-16 h-16 rounded-lg flex items-center justify-center ${
              isDark ? 'bg-gray-700' : 'bg-gray-100'
            }`}>
              <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <div>
              <h3 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {service.name}
              </h3>
              <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                {service.duration_min} minutes
              </p>
              {service.description && (
                <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  {service.description}
                </p>
              )}
            </div>
          </div>

          {/* Date & Time */}
          <div className="flex items-start space-x-4">
            <div className={`w-16 h-16 rounded-lg flex items-center justify-center ${
              isDark ? 'bg-gray-700' : 'bg-gray-100'
            }`}>
              <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <h3 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {formatDateTime(appointmentTime)}
              </h3>
              <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                {getSalonTimezone()}
              </p>
            </div>
          </div>

          {/* Booking Status */}
          <div className="flex items-center space-x-4">
            <div className={`w-16 h-16 rounded-lg flex items-center justify-center ${
              booking.status === 'confirmed' 
                ? isDark ? 'bg-green-900/20' : 'bg-green-100'
                : isDark ? 'bg-yellow-900/20' : 'bg-yellow-100'
            }`}>
              <svg className={`h-8 w-8 ${
                booking.status === 'confirmed'
                  ? isDark ? 'text-green-400' : 'text-green-600'
                  : isDark ? 'text-yellow-400' : 'text-yellow-600'
              }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h3 className={`text-lg font-medium capitalize ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {booking.status === 'confirmed' ? t('booking.status_confirmed') : t('booking.status_pending')}
              </h3>
              <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                {booking.status === 'confirmed'
                  ? t('booking.status_confirmed_desc')
                  : t('booking.status_pending_desc')
                }
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        {/* Add to Calendar */}
        <button
          onClick={generateICS}
          className={`p-4 rounded-lg border-2 transition-all duration-200 ${
            isDark 
              ? 'border-gray-700 hover:border-gray-600 bg-gray-800 hover:bg-gray-700' 
              : 'border-gray-200 hover:border-gray-300 bg-white hover:bg-gray-50'
          }`}
        >
          <div className="flex flex-col items-center space-y-2">
            <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t('booking.add_to_calendar')}
            </span>
            <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {t('booking.download_ics')}
            </span>
          </div>
        </button>

        {/* View My Bookings */}
        <button
          onClick={handleViewBookings}
          className={`p-4 rounded-lg border-2 transition-all duration-200 ${
            isDark 
              ? 'border-gray-700 hover:border-gray-600 bg-gray-800 hover:bg-gray-700' 
              : 'border-gray-200 hover:border-gray-300 bg-white hover:bg-gray-50'
          }`}
        >
          <div className="flex flex-col items-center space-y-2">
            <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t('booking.view_my_bookings')}
            </span>
            <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {t('booking.manage_appointments')}
            </span>
          </div>
        </button>

        {/* New Booking */}
        <button
          onClick={handleNewBooking}
          className={`p-4 rounded-lg border-2 transition-all duration-200 ${
            isDark 
              ? 'border-gray-700 hover:border-gray-600 bg-gray-800 hover:bg-gray-700' 
              : 'border-gray-200 hover:border-gray-300 bg-white hover:bg-gray-50'
          }`}
        >
          <div className="flex flex-col items-center space-y-2">
            <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t('booking.new_booking')}
            </span>
            <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {t('booking.book_another')}
            </span>
          </div>
        </button>
      </div>

      {/* Confirmation Note */}
      <div className={`p-4 rounded-lg border ${
        isDark ? 'bg-[rgba(125,102,51,.15)] border-[rgba(125,102,51,.35)]' : 'bg-amber-50 border-amber-200'
      }`}>
        <div className="flex items-start space-x-3">
          <svg className={`h-5 w-5 mt-0.5 ${
            isDark ? 'text-amber-300' : 'text-amber-600'
          }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <h3 className={`font-medium mb-1 ${
              isDark ? 'text-amber-200' : 'text-amber-800'
            }`}>
              {t('booking.whats_next')}
            </h3>
            <div className={`text-sm space-y-1 ${
              isDark ? 'text-amber-200' : 'text-amber-700'
            }`}>
              <p>• {t('booking.whats_next_line1')}</p>
              <p>• {t('booking.whats_next_line2')}</p>
              <p>• {t('booking.whats_next_line3')}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingSuccessPage;
